import PropTypes from 'prop-types';
import { Skeleton, Spin } from 'antd';
import './primitives.css';

/**
 * LoadingState
 *
 * Replaces the inconsistent mix of `<Spin size="large">`,
 * `<Skeleton active rows=8 />`, and bare empty divs that pages currently use
 * during initial load.
 *
 * The skeletons sit on the same grid the real content will: card padding 12px,
 * 8px between siblings (`docs/design-language.md` §3), so the page does not
 * visibly re-space itself the moment data lands.
 *
 * Variants:
 *   - page    : full-page loading (centered spinner) – use during full route loads
 *   - cards   : skeleton grid of N cards – matches dashboards / catalog grids
 *   - table   : skeleton rows mimicking a table
 *   - list    : skeleton rows for vertical lists
 *   - inline  : small inline spinner – use inside a panel
 */
const LoadingState = ({ variant = 'page', count = 6, className = '' }) => {
  if (variant === 'inline') {
    return (
      <div className={`lms-loading lms-loading--inline ${className}`.trim()}>
        <Spin />
      </div>
    );
  }

  if (variant === 'page') {
    return (
      <div className={`lms-loading lms-loading--page ${className}`.trim()}>
        <Spin size="large" />
      </div>
    );
  }

  if (variant === 'table') {
    return (
      <div className={`lms-loading lms-loading--table ${className}`.trim()}>
        <Skeleton active title={false} paragraph={{ rows: count, width: '100%' }} />
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className={`lms-loading lms-loading--list ${className}`.trim()}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="lms-loading__list-row">
            <Skeleton.Avatar active size={32} shape="square" />
            <Skeleton active title={false} paragraph={{ rows: 1, width: '70%' }} />
          </div>
        ))}
      </div>
    );
  }

  // cards (default grid)
  return (
    <div className={`lms-loading lms-loading--cards ${className}`.trim()}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="lms-loading__card">
          <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 2 }} />
        </div>
      ))}
    </div>
  );
};

LoadingState.propTypes = {
  variant: PropTypes.oneOf(['page', 'cards', 'table', 'list', 'inline']),
  count: PropTypes.number,
  className: PropTypes.string,
};

export default LoadingState;
